/**
 * Shared funding-mechanism comparison helpers for the Suggestion Engines.
 *
 * The whole point of a Suggestion Engine is to look at the realistic ways to fund a cash outlay and let the
 * projected outcome decide — accounting for liquidation costs (CGT on shares) AND the return you give up on
 * each asset. A pensioner should usually draw a tax-free account-based pension (ABP) before selling shares and
 * realising CGT — but not always (preserving a tax-free, compounding ABP can win when the CGT is small). So we
 * generate BOTH paths as candidates and let the engine rank them, rather than hard-coding a single draw order.
 *
 * Used by the granny-flat, retirement-village and downsizer modules. Funding maps use the same `type-id` → gross
 * amount shape the manual asset pickers / liquidateChosenAssets use.
 */
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import type { AssetType } from '../../../shared/src/calculation/factFindTypes.ts';
import { INVESTMENT_PROFILES } from '../../../shared/src/calculation/investmentProfiles.ts';
import { assetDisplayName } from '../utils/assetDisplayName.ts';

/** `type-id` → gross amount to draw from that asset. */
export type FundingMap = Record<string, number>;

export interface FundingVariant {
    key: string;
    nameSuffix: string;
    note: string;
    funding: FundingMap;
}

/** Total value held in a liquid asset type. */
export const sumAssetType = (factFind: DraftFactFind | null | undefined, type: AssetType | string): number =>
    (factFind?.assets?.[type] || []).reduce((s, a) => s + (a.value || 0), 0);

/** Build a chosen-asset funding map raising `target` (gross) by drawing the given types in order. */
export function drawFundingMap(
    factFind: DraftFactFind | null | undefined,
    target: number,
    typeOrder: (AssetType | string)[],
    reserve = 0,
): FundingMap {
    let remaining = target || 0;
    const map: FundingMap = {};
    (typeOrder || []).forEach((type) => {
        if (remaining <= 0) return;
        const assets = factFind?.assets?.[type] || [];
        if (type === 'bankAccounts' && reserve > 0) {
            // Keep an emergency reserve in the bank — only the amount above the reserve is available to draw.
            const totalBank = assets.reduce((s, a) => s + (a.value || 0), 0);
            let avail = Math.max(0, totalBank - reserve);
            assets.forEach((a) => {
                if (remaining <= 0 || avail <= 0) return;
                const val = a.value || 0;
                const take = Math.min(val, remaining, avail);
                if (take > 0) { map[`${type}-${a.id}`] = Math.round(take); remaining -= take; avail -= take; }
            });
            return;
        }
        assets.forEach((a) => {
            if (remaining <= 0) return;
            const val = a.value || 0;
            const take = Math.min(val, remaining);
            if (take > 0) { map[`${type}-${a.id}`] = Math.round(take); remaining -= take; }
        });
    });
    return map;
}

/*
 * EVERY WAY TO DRAW IT, TESTED (village review decision 10, owner 21 Oct 2026: "this is important and its an
 * expectation… which account based pension do you sell down… we look at all the strategy combinations").
 * Previously at most two ways were tried (pension before shares, or shares before pension) and two pensions
 * were drawn in file order. Now spare cash goes first (the bank above the emergency money, then term
 * deposits), then every order of pensions and shares is tried, and within it every order of the pensions
 * themselves, so a couple's two pensions with different balances and growth are each tried first. Orders that draw exactly the same
 * amounts from the same accounts are one way, not several, so the list holds only real differences; the
 * projection of each decides. The usual order comes first: bank, term deposits, the pension earning least
 * (the engine's own rule for a shortfall, F.46), shares.
 */
const LIQUID_KINDS = ['bankAccounts', 'termDeposits', 'accountBasedPensions', 'shares'] as const;
/** The most distinct ways kept, so the field stays a size the engine can project. */
export const MAX_FUNDING_VARIANTS = 24;
/** Above this many pensions, only the usual pension order is tried (4 pensions would be 24 orders each). */
const MAX_PENSIONS_PERMUTED = 3;

function permutations<T>(arr: T[]): T[][] {
    if (arr.length <= 1) return [arr.slice()];
    const out: T[][] = [];
    arr.forEach((x, i) => {
        permutations([...arr.slice(0, i), ...arr.slice(i + 1)]).forEach((p) => out.push([x, ...p]));
    });
    return out;
}

/** A pension started before 1 January 2015 keeps its old income-test treatment, so it is drawn last (abpPlugin.abpDrawOrder). */
const pensionOld = (a: { commencementDateOption?: unknown }): number => (a.commencementDateOption === 'pre-2015' ? 1 : 0);

/** The rate a pension is grown at in the projection: its own, or the Balanced preset when unset. */
function pensionRate(a: { growthRate?: unknown }): number {
    const r = a.growthRate;
    return r == null || r === '' || !Number.isFinite(Number(r)) ? INVESTMENT_PROFILES.balanced.pensionNetPct : Number(r);
}

type Step = { type: string; asset: { id?: unknown; value?: unknown; description?: unknown } };

function drawSteps(steps: Step[], target: number, reserve: number, bankTotal: number): { map: FundingMap; raised: number; drawn: Step[] } {
    let remaining = target || 0;
    let bankRoom = Math.max(0, bankTotal - reserve);
    const map: FundingMap = {};
    const drawn: Step[] = [];
    steps.forEach((s) => {
        if (remaining <= 0) return;
        let take = Math.min(Number(s.asset.value) || 0, remaining);
        if (s.type === 'bankAccounts') { take = Math.min(take, bankRoom); bankRoom -= take; }
        take = Math.round(take);
        if (take > 0) { map[`${s.type}-${s.asset.id}`] = take; remaining -= take; drawn.push(s); }
    });
    return { map, raised: (target || 0) - Math.max(0, remaining), drawn };
}

const KIND_WORD: Record<string, string> = { bankAccounts: 'the bank', termDeposits: 'term deposits', accountBasedPensions: 'the pension', shares: 'shares' };

function drawWords(drawn: Step[], pensionCount: number, factFind?: DraftFactFind | null): string {
    const parts: string[] = [];
    drawn.forEach((s) => {
        const word = s.type === 'accountBasedPensions' && pensionCount > 1
            ? assetDisplayName(s.asset as never, factFind, 'a pension')
            : KIND_WORD[s.type] || s.type;
        if (parts[parts.length - 1] !== word) parts.push(word);
    });
    return parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} then ${parts[parts.length - 1]}`;
}

/**
 * Every distinct way to raise `target` from the liquid accounts, the usual order first. Each variant: { key,
 * nameSuffix, note, funding } where `funding` is a `type-id`→amount map. One variant (key '') when there is no
 * real choice. Keys keep the risk panel's reading of ids: '-abp' when a pension is drawn before any shares,
 * '-shares' (at the end) when shares go first; further ways with the same lead are numbered ('-2-abp').
 * When no order raises the whole amount, the usual order alone is returned, raising what it can.
 */
export function buildFundingVariants(
    factFind: DraftFactFind | null | undefined,
    target: number,
    reserve = 0,
): FundingVariant[] {
    const assets = (factFind?.assets || {}) as Record<string, Array<{ id?: unknown; value?: unknown; description?: unknown; growthRate?: unknown; commencementDateOption?: unknown }> | undefined>;
    const bankTotal = sumAssetType(factFind, 'bankAccounts');
    const held = (k: string) => (k === 'bankAccounts' ? bankTotal - reserve : sumAssetType(factFind, k)) > 0;
    const kinds = LIQUID_KINDS.filter(held);
    const pensions = (assets.accountBasedPensions || []).filter((a) => (Number(a.value) || 0) > 0)
        .map((a, i) => ({ a, i })).sort((x, y) => pensionOld(x.a) - pensionOld(y.a) || pensionRate(x.a) - pensionRate(y.a) || x.i - y.i).map((x) => x.a);
    const pensionOrders = pensions.length > 1 && pensions.length <= MAX_PENSIONS_PERMUTED ? permutations(pensions) : [pensions];
    const steps = (kindOrder: string[], pensionOrder: typeof pensions): Step[] => kindOrder.flatMap((k) =>
        k === 'accountBasedPensions' ? pensionOrder.map((asset) => ({ type: k, asset })) : (assets[k] || []).map((asset) => ({ type: k, asset })));

    const usual = drawSteps(steps([...LIQUID_KINDS], pensions), target, reserve, bankTotal);
    const seen = new Set<string>();
    const sigOf = (m: FundingMap) => Object.keys(m).sort().map((k) => `${k}:${m[k]}`).join('|');
    const ways: Array<{ map: FundingMap; drawn: Step[]; sig: string }> = [];
    // Spare cash goes first, always: spending it costs no tax and gives up the least return, so a plan that
    // sells shares or spends a pension while spare cash sits idle is never the better one. Every order of
    // what comes after it is tried.
    const cashKinds = kinds.filter((k) => k === 'bankAccounts' || k === 'termDeposits');
    const restKinds = kinds.filter((k) => k !== 'bankAccounts' && k !== 'termDeposits');
    for (const restOrder of permutations([...restKinds] as string[])) {
        const kindOrder = [...cashKinds, ...restOrder];
        for (const pensionOrder of pensionOrders) {
            const r = drawSteps(steps(kindOrder, pensionOrder), target, reserve, bankTotal);
            if (r.raised < (target || 0) - 1) continue;
            const sig = sigOf(r.map);
            if (seen.has(sig)) continue;
            seen.add(sig);
            ways.push({ map: r.map, drawn: r.drawn, sig });
        }
    }
    if (ways.length === 0) return [{ key: '', nameSuffix: '', note: '', funding: usual.map }];
    // The usual order leads when it raises the whole amount.
    const usualSig = sigOf(usual.map);
    ways.sort((a, b) => Number(b.sig === usualSig) - Number(a.sig === usualSig));
    const kept = ways.slice(0, MAX_FUNDING_VARIANTS);
    if (kept.length === 1) return [{ key: '', nameSuffix: '', note: '', funding: kept[0]!.map }];
    const leadCount: Record<string, number> = {};
    return kept.map((w) => {
        const firstAbp = w.drawn.findIndex((s) => s.type === 'accountBasedPensions');
        const firstShares = w.drawn.findIndex((s) => s.type === 'shares');
        const lead = firstAbp >= 0 && (firstShares < 0 || firstAbp < firstShares) ? 'abp' : firstShares >= 0 ? 'shares' : '';
        const n = (leadCount[lead] = (leadCount[lead] || 0) + 1);
        const key = lead === 'abp' ? (n === 1 ? '-abp' : `-${n}-abp`) : lead === 'shares' ? (n === 1 ? '-shares' : `-${n}-shares`) : (n === 1 ? '-bank' : `-${n}-bank`);
        // Owners' names on a couple's same-named pensions: "MLC then MLC" named two different plans alike.
        const words = drawWords(w.drawn, pensions.length, factFind);
        const sellsShares = firstShares >= 0;
        return {
            key,
            nameSuffix: ` — from ${words}`,
            note: `Drawn from ${words}.${sellsShares ? ' Selling shares can mean capital gains tax; a pension can be drawn tax-free from 60.' : ''}`,
            funding: w.map,
        };
    });
}
